/** V146/V147 (IMPRESION) — helpers de API: impresoras + personalización del ticket. */
import QRCode from 'qrcode';
import { api, apiUpload } from '../lib/api';
import { EscPos, TicketBrandingEsc } from '../lib/escpos';
import { ticketDocument, PaperWidth, TicketBrandingHtml } from './ticket';
import { printToPrinter, PrintOutcome } from './router';
import { Printer, PrinterBackend, PrinterRole } from './types';

export interface PrinterInput {
  branchId?: string | null;
  name: string;
  role: PrinterRole;
  backendKind: PrinterBackend;
  paperWidth?: number;
  qzPrinterName?: string | null;
  netHost?: string | null;
  netPort?: number | null;
  isActive?: boolean;
  sortOrder?: number;
}

export async function fetchPrinters(branchId?: string | null): Promise<Printer[]> {
  const q = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
  return api<Printer[]>(`/printers${q}`);
}
export const createPrinter = (input: PrinterInput) =>
  api<Printer>('/printers', { method: 'POST', body: input });
export const updatePrinter = (id: string, input: Partial<PrinterInput>) =>
  api<Printer>(`/printers/${id}`, { method: 'PATCH', body: input });
export const deletePrinter = (id: string) =>
  api<{ deleted: true }>(`/printers/${id}`, { method: 'DELETE' });

// ===========================================================================
// V147 — personalización del ticket (por tenant/sucursal, en el servidor).
// ===========================================================================
export interface TicketSettings {
  branchId: string | null;
  headerText: string | null;
  footerText: string | null;
  showInvoiceQr: boolean;
  showIssuerLine: boolean;   // V153 (Pink): imprimir "Emisor: <razon> · RFC <rfc>". Default true.
  printComanda?: boolean;    // V180: imprimir la comanda al enviar a cocina (override).
  showCustomQr: boolean;
  customQrUrl: string | null;
  customQrLabel: string | null;
  qrSizeMm: number;
  logoHeightMm: number;
  logoMono58: string | null; logoEscpos58: string | null;
  logoMono80: string | null; logoEscpos80: string | null;
}

/** Tamaño de QR (mm) → módulo ESC/POS (dots por módulo), asumiendo ~33 módulos (v4) a 203dpi. */
export function qrModuleFromMm(mm: number): number {
  return Math.min(12, Math.max(3, Math.round((mm || 30) * 8 / 33)));
}

export async function fetchTicketSettings(branchId?: string | null, effective = false): Promise<TicketSettings> {
  const qs = new URLSearchParams();
  if (branchId) qs.set('branchId', branchId);
  if (effective) qs.set('effective', '1');
  const q = qs.toString();
  return api<TicketSettings>(`/ticket-settings${q ? '?' + q : ''}`);
}
export const saveTicketSettings = (body: Partial<TicketSettings> & { branchId?: string | null }) =>
  api<TicketSettings>('/ticket-settings', { method: 'PUT', body });
export const uploadTicketLogo = (branchId: string | null, file: File) =>
  apiUpload<TicketSettings>(`/ticket-settings/logo${branchId ? `?branchId=${encodeURIComponent(branchId)}` : ''}`, file, 'logo');
export const deleteTicketLogo = (branchId: string | null) =>
  api<TicketSettings>(`/ticket-settings/logo${branchId ? `?branchId=${encodeURIComponent(branchId)}` : ''}`, { method: 'DELETE' });

/** Resuelve la branding lista para imprimir: pickers por ancho + QR configurable (data-URL). */
export interface ResolvedBranding {
  showInvoiceQr: boolean;
  qrSizeMm: number;
  qrModule: number;
  escFor: (w: PaperWidth) => TicketBrandingEsc;
  htmlFor: (w: PaperWidth) => TicketBrandingHtml;
}
export async function resolveBranding(s: TicketSettings): Promise<ResolvedBranding> {
  const sizeMm = s.qrSizeMm || 30;
  const qrModule = qrModuleFromMm(sizeMm);
  const custom = s.showCustomQr && s.customQrUrl ? { url: s.customQrUrl, label: s.customQrLabel ?? '' } : null;
  const customQrDataUrl = custom ? await QRCode.toDataURL(custom.url, { margin: 1, width: 240 }) : null;
  return {
    showInvoiceQr: s.showInvoiceQr,
    qrSizeMm: sizeMm,
    qrModule,
    escFor: (w) => ({
      logoEscposB64: w === 58 ? s.logoEscpos58 : s.logoEscpos80,
      header: s.headerText, footer: s.footerText, customQr: custom,
    }),
    htmlFor: (w) => ({
      logoDataUrl: w === 58 ? s.logoMono58 : s.logoMono80,
      header: s.headerText, footer: s.footerText,
      customQr: custom && customQrDataUrl ? { qrDataUrl: customQrDataUrl, label: custom.label, sizeMm } : null,
    }),
  };
}

/** Prueba de impresión a UNA impresora: mismo pipeline real (backend + ancho). */
export function testPrint(p: Printer): Promise<PrintOutcome> {
  const stamp = new Date().toLocaleString('es-MX');
  const escpos = (w: PaperWidth) =>
    new EscPos(w === 58 ? 32 : 48)
      .init()
      .align('c').bold(true).size('tall').text('PRUEBA').size('normal')
      .text(p.name).bold(false)
      .align('l').line()
      .text('Si lees esto en papel,').text('la impresora quedo lista.')
      .line()
      .align('c').text(stamp)
      .cut().encode();
  const html = (w: PaperWidth) =>
    ticketDocument(
      w,
      'Prueba de impresión',
      `<h1>PRUEBA</h1><div class="sub">${p.name}</div><hr class="hr">` +
      `<p class="c">Si lees esto en papel,<br>la impresora quedó lista.</p><hr class="hr">` +
      `<p class="foot">${stamp}</p>`,
    );
  return printToPrinter(p, escpos, html);
}
